import { getFunctions, httpsCallable } from "firebase/functions";
import app, { auth } from "./firebase";
export const MEMBER_URL =
  process.env.REACT_APP_MEMBER_APP_URL ||
  "https://lodge-dining-members.web.app";
const memberOrigins = [
  new URL(MEMBER_URL).origin,
  "https://lodge-dining-members.web.app",
  "https://lodge-dining-members.firebaseapp.com",
];
const mainOrigins = [
  "https://lodge-dining.web.app",
  "https://lodge-dining.firebaseapp.com",
  "https://17parkstreetorders.co.uk",
  "https://www.17parkstreetorders.co.uk",
];
const allowed = (origin) => [...memberOrigins, ...mainOrigins].includes(origin);
const call = (name, data) =>
  httpsCallable(getFunctions(app, "europe-west2"), name, { timeout: 60000 })(
    data,
  ).then((result) => result.data);
const base64 = (bytes) =>
  btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/g, "");
export function switchPortal(target) {
  const origin =
    target === "members"
      ? new URL(MEMBER_URL).origin
      : "https://lodge-dining.web.app";
  window.location.assign(
    `${origin}/#signin-from=${encodeURIComponent(window.location.origin)}`,
  );
}
export async function processPortalHandoff(user) {
  const fragment = new URLSearchParams(window.location.hash.slice(1));
  if (user && !fragment.has("portal-request")) {
    const stored = JSON.parse(
      sessionStorage.getItem("portal-outgoing") || "null",
    );
    sessionStorage.removeItem("portal-outgoing");
    if (stored && Date.now() - stored.at <= 300000)
      fragment.set("portal-request", JSON.stringify(stored.request));
  }
  if (fragment.has("portal-code")) {
    const pending = JSON.parse(sessionStorage.getItem("portal-pkce") || "null");
    sessionStorage.removeItem("portal-pkce");
    history.replaceState(
      null,
      "",
      window.location.pathname + window.location.search,
    );
    if (
      !pending ||
      pending.state !== fragment.get("state") ||
      Date.now() - pending.at > 300000
    )
      throw Error("The sign-in handoff expired. Request a magic link.");
    const result = await call("portalExchangeHandoff", {
      code: fragment.get("portal-code"),
      state: pending.state,
      verifier: pending.verifier,
    });
    const { signInWithCustomToken } = await import("firebase/auth");
    const credential = await signInWithCustomToken(auth, result.token);
    // Re-authenticating the same UID does not emit onAuthStateChanged.
    // Return the signed-in user so callers finish loading in this callback too.
    return { user: credential.user };
  }
  if (fragment.has("signin-from")) {
    const source = fragment.get("signin-from");
    if (!allowed(source) || source === window.location.origin)
      throw Error("Invalid application destination.");
    const verifier = base64(crypto.getRandomValues(new Uint8Array(32))),
      state = base64(crypto.getRandomValues(new Uint8Array(24))),
      challenge = base64(
        new Uint8Array(
          await crypto.subtle.digest(
            "SHA-256",
            new TextEncoder().encode(verifier),
          ),
        ),
      );
    sessionStorage.setItem(
      "portal-pkce",
      JSON.stringify({ verifier, state, at: Date.now() }),
    );
    const request = { targetOrigin: window.location.origin, challenge, state };
    window.location.replace(
      `${source}/#portal-request=${encodeURIComponent(JSON.stringify(request))}`,
    );
    return true;
  }
  if (fragment.has("portal-request")) {
    const request = JSON.parse(fragment.get("portal-request"));
    if (
      !allowed(request.targetOrigin) ||
      request.targetOrigin === window.location.origin
    )
      throw Error("Invalid application destination.");
    if (!user) {
      sessionStorage.setItem(
        "portal-outgoing",
        JSON.stringify({ request, at: Date.now() }),
      );
      history.replaceState(
        null,
        "",
        window.location.pathname + window.location.search,
      );
      return false;
    }
    const result = await call("portalCreateHandoff", request);
    history.replaceState(null, "", window.location.pathname);
    window.location.replace(
      `${request.targetOrigin}/#portal-code=${result.code}&state=${encodeURIComponent(request.state)}`,
    );
    return true;
  }
  return false;
}
export const hasPortalHandoff = () =>
  /portal-code|portal-request|signin-from/.test(window.location.hash);
export async function routeMemberFromMain() {
  const session = await call("portalSession", {});
  if (session.lodges.length) {
    switchPortal("members");
    return true;
  }
  return false;
}
